import React from 'react';
import { Button, Card } from 'react-bootstrap';

import { Referto, Profile, RefertoDetail, MedicoReferto, Paziente, Clienti } from '../types';
import { apiService } from '../services/api';
import firmaPng from '../assets/firma/firma.png';
import { REFERTO_PRINT_CSS } from './refertoPrintCss';

interface RefertoPDFProps {
  referto: Referto;
  profile: Profile;
  referto_detail?: RefertoDetail[];
  clienti?: Clienti;
  storicoId: number; // il PDF generato viene salvato su questo referto
  autoSalva?: boolean; // genera e salva il PDF appena montato, senza scaricarlo
  onSalvato?: (ok: boolean) => void;
}

const RefertoPDF: React.FC<RefertoPDFProps> = ({ referto, profile, referto_detail, clienti, storicoId, autoSalva, onSalvato }) => {
  const printRef = React.useRef<HTMLDivElement>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = React.useState(false);

  // Funzione per organizzare i dati in modo consistente tra anteprima e PDF
  const organizeRefertoData = () => {
    // Gestione sicura dei valori selezionati
    const valoriSelezionati = (referto as any)?.valori_selezionati || {};
    const refertoDetailData = referto_detail || referto.referto_detail || [];
    
    // Le misurazioni PDF non vengono più processate qui
    // ma solo nel box separato "Misurazioni PDF"

    // Validazione dei dati di input
    if (!referto || typeof referto !== 'object') {
      console.warn('Referto non valido o mancante');
      return {
        categorieClinicheData: [],
        categorieEcoData: [],
        parametriClinici: []
      };
    }

    // Categorie per Esame Clinico
    // Categorie per Esame Clinico - aggiungi queste categorie
const categorieEsameClinico = ['respiro', 'soffio cardiaco', 'soffio', 'tosse'];
    const categorieClinicheData: { [key: string]: Array<{ label: string, value: string }> } = {};
    
    // Categorie per Ecocardiografia 
    const categorieEcocardiografia = ['valvola mitrale', 'valvola tricuspide', 'ventricolo sinistro', 'pattern diastolico', 'atrio sinistro', 'sezioni destre', 'polmonare', 'aorta'];
    const categorieEcoData: { [key: string]: Array<{ label: string, value: string }> } = {};

    // Organizza dati per Esame Clinico
    try {
      Object.entries(valoriSelezionati).forEach(([categoria, valori]: [string, any]) => {
        const categoriaLower = categoria.toLowerCase();
        if (categorieEsameClinico.some(cat => categoriaLower.includes(cat.toLowerCase()))) {
          if (valori && typeof valori === 'object' && Object.keys(valori).length > 0) {
            categorieClinicheData[categoria] = Object.entries(valori)
              .filter(([campo, valore]) => valore && valore.toString().trim())
              .map(([campo, valore]) => ({ label: campo, value: valore as string }));
          }
        }
      });
    } catch (error) {
      console.warn('Errore nell\'organizzazione dei dati esame clinico:', error);
    }

    // Le misurazioni non vengono più aggiunte al box di Ecocardiografia
    // ma solo al box separato "Misurazioni PDF"

    // Organizza dati per Ecocardiografia dai valori selezionati
    try {
      Object.entries(valoriSelezionati).forEach(([categoria, valori]: [string, any]) => {
        const categoriaLower = categoria.toLowerCase();
        if (categorieEcocardiografia.some(cat => categoriaLower.includes(cat.toLowerCase()))) {
          if (valori && typeof valori === 'object' && Object.keys(valori).length > 0) {
            categorieEcoData[categoria] = Object.entries(valori)
              .filter(([campo, valore]) => valore && valore.toString().trim())
              .map(([campo, valore]) => ({ label: campo, value: valore as string }));
          }
        }
      });
    } catch (error) {
      console.warn('Errore nell\'organizzazione dei dati ecocardiografia:', error);
    }

    // Aggiungi categorie generiche per Ecocardiografia
    try {
      Object.entries(valoriSelezionati).forEach(([categoria, valori]: [string, any]) => {
        const categoriaLower = categoria.toLowerCase();
        const isEsameClinico = categorieEsameClinico.some(cat => categoriaLower.includes(cat.toLowerCase()));
        if (!isEsameClinico && !categorieEcoData[categoria]) {
          if (valori && typeof valori === 'object' && Object.keys(valori).length > 0) {
            categorieEcoData[categoria] = Object.entries(valori)
              .filter(([campo, valore]) => valore && valore.toString().trim())
              .map(([campo, valore]) => ({ label: campo, value: valore as string }));
          }
        }
      });
    } catch (error) {
      console.warn('Errore nell\'organizzazione delle categorie generiche:', error);
    }

    // Gestisci referto_detail
    try {
      if (refertoDetailData.length > 0) {
        const gruppiCategorie = refertoDetailData.reduce((acc, item) => {
          const categoriaLower = item.categoria.toLowerCase();
          const isEsameClinico = categorieEsameClinico.some(cat => categoriaLower.includes(cat.toLowerCase()));
          
          if (isEsameClinico) {
            if (!acc.clinico[item.categoria]) {
              acc.clinico[item.categoria] = [];
            }
            acc.clinico[item.categoria].push({ label: item.campo, value: item.valore });
          } else {
            if (!acc.eco[item.categoria]) {
              acc.eco[item.categoria] = [];
            }
            acc.eco[item.categoria].push({ label: item.campo, value: item.valore });
          }
          return acc;
        }, { clinico: {} as any, eco: {} as any });
        
        Object.entries(gruppiCategorie.clinico).forEach(([categoria, campi]) => {
          categorieClinicheData[categoria] = campi as Array<{ label: string, value: string }>;
        });
        
        Object.entries(gruppiCategorie.eco).forEach(([categoria, campi]) => {
          categorieEcoData[categoria] = campi as Array<{ label: string, value: string }>;
        });
      }
    } catch (error) {
      console.warn('Errore nell\'organizzazione dei dati referto_detail:', error);
    }

    // Parametri clinici statici con gestione sicura
    const parametriClinici: Array<{ label: string, value: string }> = [];
    try {
      if (referto.frequenza_cardiaca) parametriClinici.push({label: 'Frequenza Cardiaca', value: `${referto.frequenza_cardiaca} bpm`});
      if (referto.ritmo_cardiaco) parametriClinici.push({label: 'Ritmo Cardiaco', value: referto.ritmo_cardiaco});
      if (referto.mucose) parametriClinici.push({label: 'Mucose Esplorabili', value: referto.mucose});
      if (referto.trc) parametriClinici.push({label: 'TRC', value: referto.trc});
      if (referto.itto_cardiaco) parametriClinici.push({label: 'Itto Cardiaco', value: referto.itto_cardiaco});
      if (referto.polso_femorale) parametriClinici.push({label: 'Polso Femorale', value: referto.polso_femorale});
      if (referto.polso_giugulare) parametriClinici.push({label: 'Polso Giugulare', value: referto.polso_giugulare});
      if (referto.attivita) parametriClinici.push({label: 'Attività', value: referto.attivita});
      if (referto.tosse) parametriClinici.push({label: 'Tosse', value: referto.tosse});
    } catch (error) {
      console.warn('Errore nell\'organizzazione dei parametri clinici:', error);
    }

    try {
      return {
        categorieClinicheData: Object.entries(categorieClinicheData).filter(([_, valori]) => valori.length > 0),
        categorieEcoData: Object.entries(categorieEcoData).filter(([_, valori]) => valori.length > 0),
        parametriClinici
      };
    } catch (error) {
      console.warn('Errore nel return dei dati organizzati:', error);
      return {
        categorieClinicheData: [],
        categorieEcoData: [],
        parametriClinici: []
      };
    }
  };

  // Funzione per ottenere i dati del medico in modo sicuro
  const getMedicoData = (): MedicoReferto | Profile => {
    if (!referto || !referto.medico) {
      return profile;
    }
    return referto.medico;
  };

  // Funzione per convertire un'immagine in base64 tramite proxy del backend
  const convertImageToBase64 = async (imageUrl: string): Promise<string | null> => {
    try {
      console.log('🖼️ [PDF] Conversione immagine via proxy backend:', imageUrl);
      
      // Usa il metodo API per il proxy delle immagini
      const blob = await apiService.proxyImage(imageUrl);
      
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          console.log('✅ [PDF] Immagine convertita in base64 con successo');
          resolve(reader.result as string);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('❌ [PDF] Errore conversione immagine tramite proxy:', error);
      return null;
    }
  };

  // Funzione per convertire un file locale in base64
  const convertLocalImageToBase64 = async (imagePath: string): Promise<string | null> => {
    try {
      console.log('🖼️ [PDF] Conversione immagine locale:', imagePath);
      
      const response = await fetch(imagePath);
      const blob = await response.blob();
      
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          console.log('✅ [PDF] Immagine locale convertita in base64 con successo');
          resolve(reader.result as string);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('❌ [PDF] Errore conversione immagine locale:', error);
      return null;
    }
  };

  // Funzione per ottenere l'URL del logo in modo sicuro
  const getLogoUrl = (): string | null => {
    try {
      const medicoData = getMedicoData();
      console.log('medico data', medicoData);
      if ('logo_attivo' in medicoData && medicoData.logo_attivo?.image_url) {
        return medicoData.logo_attivo.image_url;
      }
      if ('logo' in medicoData && medicoData.logo?.image_url) {
        return medicoData.logo.image_url;
      }
      return null;
    } catch (error) {
      console.warn('Errore nel recupero del logo:', error);
      return null;
    }
  };

  // Funzione per ottenere il nome del medico in modo sicuro
  const getMedicoName = (): string => {
    try {
      const medicoData = getMedicoData();
      const firstName = 'first_name' in medicoData ? medicoData.first_name : '';
      const lastName = 'last_name' in medicoData ? medicoData.last_name : '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || 'Medico non specificato';
    } catch (error) {
      console.warn('Errore nel recupero del nome medico:', error);
      return 'Medico non specificato';
    }
  };

  // Funzione per ottenere i dati del paziente in modo sicuro
  const getPazienteData = (): Partial<Paziente> => {
    try {
      if (!referto || !referto.paziente) {
        console.warn('Dati paziente mancanti');
        return {};
      }
      return referto.paziente;
    } catch (error) {
      console.warn('Errore nel recupero dei dati paziente:', error);
      return {};
    }
  };

  // Funzione per ottenere i dati del cliente in modo sicuro
  const getClienteData = (): any => {
    try {
      // Prova prima il parametro clienti passato al componente
      if (clienti) {
        return clienti;
      }
      // Prova nell'oggetto StoricoReferti (clienti)
      if ((referto as any).clienti) {
        return (referto as any).clienti;
      }
      // Fallback su paziente.cliente
      if (referto?.paziente?.cliente) {
        return referto.paziente.cliente;
      }
      console.warn('Dati cliente mancanti');
      return {};
    } catch (error) {
      console.warn('Errore nel recupero dei dati cliente:', error);
      return {};
    }
  };

  // Funzione per ottenere il medico referente in modo sicuro
  const getMedicoReferente = (): string => {
    try {
      if (!referto) {
        return 'Non specificato';
      }

      if (referto.medico_referente) {
        if (typeof referto.medico_referente === 'string') {
          return referto.medico_referente;
        }
        if (referto.medico_referente.nome && referto.medico_referente.cognome) {
          return `Dr. ${referto.medico_referente.nome} ${referto.medico_referente.cognome}`;
        }
        if (referto.medico_referente.nome) {
          return `Dr. ${referto.medico_referente.nome}`;
        }
      }
      
      // Fallback per altri campi possibili
      const medicoRef = (referto as any).medico_ref;
      if (medicoRef) {
        return typeof medicoRef === 'string' ? medicoRef : `Dr. ${medicoRef}`;
      }
      
      return 'Non specificato';
    } catch (error) {
      console.warn('Errore nel recupero del medico referente:', error);
      return 'Non specificato';
    }
  };

  // Funzione per ottenere la terapia in modo sicuro
  const getTerapia = (): string => {
    try {
      if (!referto) {
        return 'Non specificata';
      }
      return referto.terapia_in_atto || (referto as any).terapia_attuale || 'Non specificata';
    } catch (error) {
      console.warn('Errore nel recupero della terapia:', error);
      return 'Non specificata';
    }
  };

  // Funzione per ottenere le conclusioni in modo sicuro
  const getConclusioni = (): string => {
    try {
      if (!referto) {
        return 'Non specificate';
      }
      return referto.conclusioni || (referto as any).conclusioni_referto || 'Non specificate';
    } catch (error) {
      console.warn('Errore nel recupero delle conclusioni:', error);
      return 'Non specificate';
    }
  };

  // Funzione per ottenere le indicazioni in modo sicuro
  const getIndicazioni = (): string => {
    try {
      if (!referto) {
        return 'Non specificate';
      }
      return referto.indicazioni || (referto as any).indicazioni_terapeutiche || 'Non specificate';
    } catch (error) {
      console.warn('Errore nel recupero delle indicazioni:', error);
      return 'Non specificate';
    }
  };

  // Funzione per ottenere il controllo in modo sicuro
  const getControllo = (): string => {
    try {
      if (!referto) {
        return '';
      }
      return referto.controllo || (referto as any).prossimo_controllo || '';
    } catch (error) {
      console.warn('Errore nel recupero del controllo:', error);
      return '';
    }
  };

  // Funzione per ottenere le note esame in modo sicuro
  const getNoteEsame = (): string => {
    try {
      if (!referto) {
        return '';
      }
      return (referto as any).note_esame || referto.note || '';
    } catch (error) {
      console.warn('Errore nel recupero delle note esame:', error);
      return 'Non specificate';
    }
  };

  // Funzione per ottenere le note esame specifiche
  const getNoteEsameSpecifiche = (): string => {
    try {
      if (!referto) {
        return '';
      }
      return (referto as any).note_esame || '';
    } catch (error) {
      console.warn('Errore nel recupero delle note esame specifiche:', error);
      return '';
    }
  };

  // Funzione per ottenere le note generali
  const getNoteGenerali = (): string => {
    try {
      if (!referto) {
        return '';
      }
      return referto.note || '';
    } catch (error) {
      console.warn('Errore nel recupero delle note generali:', error);
      return '';
    }
  };

  // Crea il PDF dal contenuto mostrato (resa lato server dell'HTML)
  const creaPdf = async (): Promise<{ pdfBlob: Blob; nomeFile: string }> => {
    const content = printRef.current;
    if (!content) {
      throw new Error('Elemento referto non trovato');
    }

    let originalLogoSrc: string | null = null;
    let originalFirmaSrc: string | null = null;
    const logoImg = content.querySelector('img[alt="Logo Medico"]') as HTMLImageElement | null;
    const firmaImg = content.querySelector('img[alt="Firma Medico"]') as HTMLImageElement | null;

    try {
      const logoUrl = getLogoUrl();
      if (logoUrl && logoImg) {
        const logoBase64 = await convertImageToBase64(logoUrl);
        if (logoBase64) {
          originalLogoSrc = logoImg.src;
          logoImg.src = logoBase64;
        }
      }

      if (firmaImg) {
        const firmaBase64 = await convertLocalImageToBase64(firmaPng);
        if (firmaBase64) {
          originalFirmaSrc = firmaImg.src;
          firmaImg.src = firmaBase64;
        }
      }

      const pazienteData = getPazienteData();
      const clienteData = getClienteData();
      const dataFormatted = referto?.data || new Date().toLocaleDateString('it-IT').replace(/\//g, '-');
      const nomeFile = `REFERTO_${pazienteData.nome || 'NOME'}_${clienteData.cognome || 'COGNOME'}_${dataFormatted}.pdf`
        .replace(/\s+/g, '')
        .toUpperCase();

      const html = `<style>${REFERTO_PRINT_CSS}</style>${content.outerHTML}`;
      const pdfBlob = await apiService.generateRefertoPdf(html);

      return { pdfBlob, nomeFile };
    } finally {
      if (originalLogoSrc && logoImg) {
        logoImg.src = originalLogoSrc;
      }
      if (originalFirmaSrc && firmaImg) {
        firmaImg.src = originalFirmaSrc;
      }
    }
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const { pdfBlob, nomeFile } = await creaPdf();

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeFile;
      link.click();
      URL.revokeObjectURL(url);

      try {
        await apiService.salvaPdfDocumento('referto', storicoId, pdfBlob, nomeFile);
      } catch (uploadError) {
        console.error('Errore durante l\'upload del PDF al server:', uploadError);
      }
    } catch (error: any) {
      console.error('Errore durante la generazione del PDF:', error);
      let message = 'Si è verificato un errore durante la generazione del PDF. Riprova.';
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          // la risposta non è JSON
        }
      }
      alert(message);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Generazione automatica (usata dalla finestra email): crea e salva il PDF senza scaricarlo
  const autoAvviato = React.useRef(false);
  React.useEffect(() => {
    if (!autoSalva || autoAvviato.current) return;
    autoAvviato.current = true;
    creaPdf()
      .then(({ pdfBlob, nomeFile }) => apiService.salvaPdfDocumento('referto', storicoId, pdfBlob, nomeFile))
      .then(() => onSalvato?.(true))
      .catch((error) => {
        console.error('Errore nella generazione automatica del PDF:', error);
        onSalvato?.(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="mb-3">
        <Button 
          variant="primary" 
          className="me-2" 
          onClick={generatePDF}
          disabled={isGeneratingPDF}
        >
          {isGeneratingPDF ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Generazione PDF...
            </>
          ) : (
            '📥 Genera PDF Referto'
          )}
        </Button>
        
      </div>

      <style>{REFERTO_PRINT_CSS}</style>
      <div ref={printRef} className="referto-sheet">
        <Card className="p-4" style={{ fontFamily: 'Arial, "Liberation Sans", Helvetica, sans-serif', lineHeight: '1.6', border: 'none' }}>
          {/* Header principale con layout a due colonne */}
          {(() => {
            const logoUrl = getLogoUrl();
            const medicoData = getMedicoData();
            
            return (
              <div className="mb-4" style={{ 
                backgroundColor: '#1891a6', 
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px'
              }}>
                <div className="row align-items-center">
                  {/* Colonna sinistra: Logo */}
                  <div className="col-md-4">
                    {logoUrl && (
                      <div style={{ maxWidth: '200px' }}>
                        <img 
                          src={logoUrl} 
                          alt="Logo Medico" 
                          style={{ 
                            maxWidth: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: '12px'
                          }}
                          onError={(e) => {
                            console.warn('Errore nel caricamento del logo:', logoUrl);
                            e.currentTarget.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('Logo caricato con successo:', logoUrl);
                          }}
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Colonna destra: Informazioni medico e data */}
                  <div className="col-md-8">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h1 className="mb-1" style={{ 
                          fontSize: '24px', 
                          fontWeight: 'bold',
                          color: 'white',
                          marginBottom: '4px'
                        }}>
                          Dr. {getMedicoName()}
                        </h1>
                        <p className="mb-1" style={{ fontSize: '14px', color: 'white', margin: '0' }}>
                          Albo TE n 499
                        </p>
                        <p className="mb-1" style={{ fontSize: '14px', color: 'white', margin: '0' }}>
                          ISVPS Cardiology Certificate
                        </p>
                        <p className="mb-1" style={{ fontSize: '14px', color: 'white', margin: '0' }}>
                          Master di II livello in Cardiologia Veterinaria
                        </p>
                        <p className="mb-0" style={{ fontSize: '14px', color: 'white', margin: '0' }}>
                          Email: {medicoData.email || 'email@esempio.com'}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p className="mb-0" style={{ fontSize: '14px', color: 'white' }}>
                          Data: <span style={{ fontWeight: 'bold' }}>
                            {referto.data || new Date().toLocaleDateString('it-IT')}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Informazioni Paziente, Dettagli Visita e Anamnesi - Stile originale */}
          <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '8px 24px 8px 24px', borderRadius: '8px' }}>
            <h2 className="mb-3" style={{ 
              fontSize: '18px', 
              fontWeight: 'bold', 
              color: '#1891a6' 
            }}>
              Dati generali
            </h2>
            
            {/* Prima riga: Dati Paziente e Dettagli Visita */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '8px'
                }}>
                  <h3 className="mb-3" style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#1891a6' 
                  }}>
                    Informazioni Paziente
                  </h3>
                  <div className="space-y-2">
                    {(() => {
                      const cliente = getClienteData();
                      const proprietario = `${cliente?.nome || ''} ${cliente?.cognome || ''}`.trim();
                      if (!proprietario) return null;
                      return (
                        <p className="mb-2">
                          <span style={{ fontWeight: '500' }}>Proprietario/a:</span>
                          <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                            {proprietario}
                          </span>
                        </p>
                      );
                    })()}
                    <p className="mb-2">
                      <span style={{ fontWeight: '500' }}>Paziente:</span> 
                      <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                        {getPazienteData().nome || 'Nome non specificato'}
                      </span>
                    </p>
                    {getPazienteData().age && (
                      <p className="mb-2">
                        <span style={{ fontWeight: '500' }}>Età:</span>
                        <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                          {getPazienteData().age}
                        </span>
                      </p>
                    )}
                    {getPazienteData().cliente?.codice_fiscale && (
                      <p className="mb-2">
                        <span style={{ fontWeight: '500' }}>CF:</span> 
                        <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                          {getPazienteData().cliente?.codice_fiscale}
                        </span>
                      </p>
                    )}
                    {getPazienteData().specie && (
                      <p className="mb-2">
                        <span style={{ fontWeight: '500' }}>Specie:</span> 
                        <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                          {getPazienteData().specie}
                        </span>
                      </p>
                    )}
                    {getPazienteData().razza && (
                      <p className="mb-2">
                        <span style={{ fontWeight: '500' }}>Razza:</span> 
                        <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                          {getPazienteData().razza}
                        </span>
                      </p>
                    )}
                    {getPazienteData().sesso && (
                      <p className="mb-2">
                        <span style={{ fontWeight: '500' }}>Sesso:</span> 
                        <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                          {getPazienteData().sesso}
                        </span>
                      </p>
                    )}
                    {getPazienteData().peso && (
                      <p className="mb-2">
                        <span style={{ fontWeight: '500' }}>Peso:</span> 
                        <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                          {getPazienteData().peso} kg
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '8px'
                }}>
                  <h3 className="mb-3" style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#1891a6' 
                  }}>
                    Dettagli Visita
                  </h3>
                  <div className="space-y-2">
                    <p className="mb-2">
                      <span style={{ fontWeight: '500' }}>Motivo:</span> 
                      <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                        {referto?.motivo_visita || 'Non specificato'}
                      </span>
                    </p>
                    <p className="mb-2">
                      <span style={{ fontWeight: '500' }}>Terapia in Atto:</span> 
                      <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                        {getTerapia()}
                      </span>
                    </p>
                    {getMedicoReferente() !== 'Non specificato' && (
                      <p className="mb-2">
                        <span style={{ fontWeight: '500' }}>Medico Referente:</span> 
                        <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                          {getMedicoReferente()}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Seconda riga: Anamnesi */}
            {referto?.anamnesi && (
              <div className="row">
                <div className="col-12">
                  <div style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '16px', 
                    borderRadius: '8px'
                  }}>
                    <h3 className="mb-3" style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold', 
                      color: '#1891a6' 
                    }}>
                      Anamnesi
                    </h3>
                    <div style={{ fontWeight: 'bold' }}>
                      {referto.anamnesi}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sezioni finali - Spostate nella prima pagina */}
          {/* Conclusioni */}
          {getConclusioni() !== 'Non specificate' && (
            <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '8px 24px 8px 24px', borderRadius: '8px' }}>
              <h2 className="mb-3" style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: '#1891a6' 
              }}>
                Conclusioni
              </h2>
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                fontWeight: 'bold'
              }}>
                {getConclusioni()}
              </div>
            </div>
          )}

          {/* Indicazioni e terapia */}
          {getIndicazioni() !== 'Non specificate' && (
            <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '8px 24px 8px 24px', borderRadius: '8px' }}>
              <h2 className="mb-3" style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: '#1891a6' 
              }}>
                Indicazioni e terapia
              </h2>
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                fontWeight: 'bold',
                whiteSpace: 'pre-line'
              }}>
                {getIndicazioni()}
              </div>
            </div>
          )}

          {/* Prossimo controllo */}
          {getControllo() !== '' && (
            <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '8px 24px 8px 24px', borderRadius: '8px' }}>
              <h2 className="mb-3" style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: '#1891a6' 
              }}>
                Prossimo controllo
              </h2>
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                fontWeight: 'bold'
              }}>
                {getControllo()}
              </div>
            </div>
          )}





          {/* Esame Clinico - Stile originale */}
          {(() => {
            const { categorieClinicheData, parametriClinici } = organizeRefertoData();
            
            if (categorieClinicheData.length === 0 && parametriClinici.length === 0) return null;
            
            return (
              <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '8px 24px 8px 24px', borderRadius: '8px' }}>
                <h2 className="mb-3" style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  color: '#1891a6' 
                }}>
                  Esame Clinico
                </h2>

                                {/* Prima riga: Parametri Clinici e Respiro */}
                <div className="row mb-3">
                  {/* Parametri Clinici */}
                  {parametriClinici.length > 0 && (
                    <div className="col-md-8">
                      <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '16px', 
                        borderRadius: '8px',
                        height: '100%'
                      }}>
                        <h3 className="mb-3" style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          color: '#1891a6' 
                        }}>
                          Parametri Clinici
                        </h3>
                        <div className="row">
                          {parametriClinici.map((param, idx) => (
                            <div key={idx} className="col-md-6 mb-2">
                              <div style={{ padding: '8px' }}>
                                <span style={{ fontWeight: '500' }}>{param.label}:</span><br />
                                <span style={{ 
                                  fontWeight: '700', 
                                  color: '#106675' 
                                }}>
                                  {param.value}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Respiro */}
                  {categorieClinicheData.find(([categoria]) => categoria.toLowerCase().includes('respiro')) && (
                    <div className="col-md-4">
                      {(() => {
                        const respiroData = categorieClinicheData.find(([categoria]) => categoria.toLowerCase().includes('respiro'));
                        if (!respiroData) return null;
                        const [categoria, valori] = respiroData;
                        return (
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '8px',
                            height: '100%'
                          }}>
                            <h3 className="mb-3" style={{ 
                              fontSize: '16px', 
                              fontWeight: 'bold', 
                              color: '#1891a6' 
                            }}>
                              {categoria}
                            </h3>
                            <div className="row">
                              {valori.map((item, idx) => (
                                <div key={idx} className="col-12 mb-2">
                                  <div style={{ padding: '8px' }}>
                                    <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: '#106675' 
                                    }}>
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Categorie Esame Clinico - Soffio cardiaco e altre */}
                {categorieClinicheData
                  .filter(([categoria]) => !categoria.toLowerCase().includes('respiro'))
                  .map(([categoria, valori], index) => (
                  <div key={categoria} className="mb-3">
                    
                    <div style={{ 
                      backgroundColor: '#f8f9fa', 
                      padding: '16px', 
                      borderRadius: '8px'
                    }}>
                      <h3 className="mb-2" style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold', 
                      color: '#1891a6' 
                    }}>
                      {categoria}
                    </h3>
                      <div className="row">
                        {valori.map((item, idx) => (
                          <div key={idx} className="col-md-4 col-lg-3 mb-2">
                            <div style={{ padding: '8px' }}>
                              <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                              <span style={{ 
                                fontWeight: '700', 
                                color: '#106675' 
                              }}>
                                {item.value}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Note Aggiuntive Esame Clinico */}
                {(getNoteEsame() !== '') && (
                  <div className="mt-3">
                   
                    <div style={{ 
                      backgroundColor: '#f8f9fa', 
                      padding: '16px', 
                      borderRadius: '8px',
                      fontWeight: 'bold'
                    }}>
                       <h3 className="mb-2" style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold', 
                      color: '#1891a6' 
                    }}>
                      Note Aggiuntive
                    </h3>
                      {getNoteEsameSpecifiche() && (
                        <div className="mb-2">
                          <strong>Note Esame:</strong>
                          <p className="mb-0 mt-1">{getNoteEsameSpecifiche()}</p>
                        </div>
                      )}
                      {getNoteGenerali() && (
                        <div>
                          <strong>Note Generali:</strong>
                          <p className="mb-0 mt-1">{getNoteGenerali()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Ecocardiografia - Stile originale */}
          {(() => {
            const { categorieEcoData } = organizeRefertoData();
            
            if (categorieEcoData.length === 0) return null;
            
            return (
              <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '24px 24px 12px 24px', borderRadius: '8px' }}>
                <h2 className="mb-3" style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  color: '#1891a6' 
                }}>
                  Ecocardiografia
                </h2>
                
                {/* Prima riga: Valvola mitrale, Valvola tricuspide, Pattern diastolico */}
                <div className="row mb-3">
                  {/* Valvola mitrale */}
                  {categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('valvola mitrale')) && (
                    <div className="col-md-4">
                      {(() => {
                        const valvolaMitraleData = categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('valvola mitrale'));
                        if (!valvolaMitraleData) return null;
                        const [categoria, valori] = valvolaMitraleData;
                        return (
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '8px',
                            height: '100%'
                          }}>
                            <h3 className="mb-3" style={{ 
                              fontSize: '16px', 
                              fontWeight: 'bold', 
                              color: '#1891a6' 
                            }}>
                              {categoria}
                            </h3>
                            <div className="row">
                              {valori.map((item, idx) => (
                                <div key={idx} className="col-12 mb-2">
                                  <div style={{ padding: '8px' }}>
                                    <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: '#106675' 
                                    }}>
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Valvola tricuspide */}
                  {categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('valvola tricuspide')) && (
                    <div className="col-md-4">
                      {(() => {
                        const valvolaTricuspideData = categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('valvola tricuspide'));
                        if (!valvolaTricuspideData) return null;
                        const [categoria, valori] = valvolaTricuspideData;
                        return (
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '8px',
                            height: '100%'
                          }}>
                            <h3 className="mb-3" style={{ 
                              fontSize: '16px', 
                              fontWeight: 'bold', 
                              color: '#1891a6' 
                            }}>
                              {categoria}
                            </h3>
                            <div className="row">
                              {valori.map((item, idx) => (
                                <div key={idx} className="col-12 mb-2">
                                  <div style={{ padding: '8px' }}>
                                    <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: '#106675' 
                                    }}>
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Pattern diastolico */}
                  {categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('pattern diastolico')) && (
                    <div className="col-md-4">
                      {(() => {
                        const patternDiastolicoData = categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('pattern diastolico'));
                        if (!patternDiastolicoData) return null;
                        const [categoria, valori] = patternDiastolicoData;
                        return (
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '8px',
                            height: '100%'
                          }}>
                            <h3 className="mb-3" style={{ 
                              fontSize: '16px', 
                              fontWeight: 'bold', 
                              color: '#1891a6' 
                            }}>
                              {categoria}
                            </h3>
                            <div className="row">
                              {valori.map((item, idx) => (
                                <div key={idx} className="col-12 mb-2">
                                  <div style={{ padding: '8px' }}>
                                    <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: '#106675' 
                                    }}>
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Ventricolo sinistro - A larghezza piena */}
                {categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('ventricolo sinistro')) && (
                  <div className="mb-3">
                    {(() => {
                      const ventricoloSinistroData = categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('ventricolo sinistro'));
                      if (!ventricoloSinistroData) return null;
                      const [categoria, valori] = ventricoloSinistroData;
                      return (
                        <>
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '8px'
                          }}>
                            <h3 className="mb-2" style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: '#1891a6' 
                          }}>
                            {categoria}
                          </h3>
                            <div className="row">
                              {valori.map((item, idx) => (
                                <div key={idx} className="col-md-4 col-lg-3 mb-2">
                                  <div style={{ padding: '8px' }}>
                                    <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: '#106675' 
                                    }}>
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Seconda riga: Atrio sinistro, Sezioni destre, Aorta */}
                <div className="row mb-3">
                  {/* Atrio sinistro */}
                  {categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('atrio sinistro')) && (
                    <div className="col-md-4">
                      {(() => {
                        const atrioSinistroData = categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('atrio sinistro'));
                        if (!atrioSinistroData) return null;
                        const [categoria, valori] = atrioSinistroData;
                        return (
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '8px',
                            height: '100%'
                          }}>
                            <h3 className="mb-3" style={{ 
                              fontSize: '16px', 
                              fontWeight: 'bold', 
                              color: '#1891a6' 
                            }}>
                              {categoria}
                            </h3>
                            <div className="row">
                              {valori.map((item, idx) => (
                                <div key={idx} className="col-12 mb-2">
                                  <div style={{ padding: '8px' }}>
                                    <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: '#106675' 
                                    }}>
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Sezioni destre */}
                  {categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('sezioni destre')) && (
                    <div className="col-md-4">
                      {(() => {
                        const sezioniDestreData = categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('sezioni destre'));
                        if (!sezioniDestreData) return null;
                        const [categoria, valori] = sezioniDestreData;
                        return (
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '8px',
                            height: '100%'
                          }}>
                            <h3 className="mb-3" style={{ 
                              fontSize: '16px', 
                              fontWeight: 'bold', 
                              color: '#1891a6' 
                            }}>
                              {categoria}
                            </h3>
                            <div className="row">
                              {valori.map((item, idx) => (
                                <div key={idx} className="col-12 mb-2">
                                  <div style={{ padding: '8px' }}>
                                    <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: '#106675' 
                                    }}>
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Aorta */}
                  {categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('aorta')) && (
                    <div className="col-md-4">
                      {(() => {
                        const aortaData = categorieEcoData.find(([categoria]) => categoria.toLowerCase().includes('aorta'));
                        if (!aortaData) return null;
                        const [categoria, valori] = aortaData;
                        return (
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px', 
                            borderRadius: '8px',
                            height: '100%'
                          }}>
                            <h3 className="mb-3" style={{ 
                              fontSize: '16px', 
                              fontWeight: 'bold', 
                              color: '#1891a6' 
                            }}>
                              {categoria}
                            </h3>
                            <div className="row">
                              {valori.map((item, idx) => (
                                <div key={idx} className="col-12 mb-2">
                                  <div style={{ padding: '8px' }}>
                                    <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: '#106675' 
                                    }}>
                                      {item.value}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Categorie Ecocardiografia - Altre categorie rimanenti */}
                {categorieEcoData
                  .filter(([categoria]) => 
                    !categoria.toLowerCase().includes('valvola mitrale') && 
                    !categoria.toLowerCase().includes('valvola tricuspide') && 
                    !categoria.toLowerCase().includes('pattern diastolico') &&
                    !categoria.toLowerCase().includes('ventricolo sinistro') &&
                    !categoria.toLowerCase().includes('atrio sinistro') &&
                    !categoria.toLowerCase().includes('sezioni destre') &&
                    !categoria.toLowerCase().includes('aorta')
                  )
                  .map(([categoria, valori], index) => (
                  <div key={categoria} className="mb-3">
                    
                    <div style={{ 
                      backgroundColor: '#f8f9fa', 
                      padding: '16px', 
                      borderRadius: '8px'
                    }}>
                      <h3 className="mb-2" style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold', 
                      color: '#1891a6' 
                    }}>
                      {categoria}
                    </h3>
                      <div className="row">
                        {valori.map((item, idx) => (
                          <div key={idx} className="col-md-4 col-lg-3 mb-2">
                            <div style={{ padding: '8px' }}>
                              <span style={{ fontWeight: '500' }}>{item.label}:</span><br />
                              <span style={{ 
                                fontWeight: '700', 
                                color: '#106675' 
                              }}>
                                {item.value}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Note Ecocardiografiche */}
                {referto?.note_eco && (
                  <div className="mt-3">
                    
                    <div style={{ 
                      backgroundColor: '#f8f9fa', 
                      padding: '16px', 
                      borderRadius: '8px',
                      fontWeight: 'bold'
                    }}>
                      <h3 className="mb-2" style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold', 
                      color: '#1891a6' 
                    }}>
                      Note Aggiuntive Ecocardiografia
                    </h3>
                      {referto.note_eco}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

{/* Misurazioni PDF - Prima pagina: Mitrale, Tricuspide, Aorta, Polmonare */}
{(() => {
  const misurazioniPDF = (referto as any).misurazioni_pdf || referto.misurazioni_pdf;
  
  let misurazioniData = {};
  if (Array.isArray(misurazioniPDF)) {
    const misurazioniPerSezione = misurazioniPDF.reduce((acc, misurazione) => {
      if (!acc[misurazione.sezione]) {
        acc[misurazione.sezione] = [];
      }
      acc[misurazione.sezione].push({
        name: misurazione.nome,
        value: misurazione.valore
      });
      return acc;
    }, {});
    misurazioniData = misurazioniPerSezione;
  } else {
    misurazioniData = misurazioniPDF || {};
  }
  
  const hasMisurazioni = Object.keys(misurazioniData).length > 0;
  
  if (!hasMisurazioni) return null;
  
  // Sezioni per la prima pagina
  const sezioniPrimaPagina = ['Mitrale', 'Tricuspide', 'Aorta', 'Polmonare'];
  const sezioniPrimaPaginaPresenti = sezioniPrimaPagina.filter(sezione => (misurazioniData as any)[sezione]);
  
  if (sezioniPrimaPaginaPresenti.length === 0) return null;
  
  return (
    <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '8px 24px 8px 24px', borderRadius: '8px' }}>
      <h2 className="mb-3" style={{ 
        fontSize: '18px', 
        fontWeight: 'bold', 
        color: '#1891a6' 
      }}>
        Misurazioni
      </h2>
      
      {sezioniPrimaPaginaPresenti.map((section) => {
        const measurements = (misurazioniData as any)[section];
        if (!Array.isArray(measurements) || measurements.length === 0) return null;
        
        return (
          <div key={section} className="mb-3">
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <h3 className="mb-2" style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: '#1891a6' 
              }}>
                {section}
              </h3>
              <div className="row">
                {measurements.map((measurement: any, idx: number) => (
                  <div key={idx} className="col-md-4 col-lg-3 mb-2">
                    <div style={{ padding: '8px' }}>
                      <span style={{ fontWeight: '500' }}>{measurement.name}:</span><br />
                      <span style={{ 
                        fontWeight: '700', 
                        color: '#106675' 
                      }}>
                        {measurement.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
})()}


{/* Misurazioni PDF - Seconda pagina: M-Mode e PW */}
{(() => {
  const misurazioniPDF = (referto as any).misurazioni_pdf || referto.misurazioni_pdf;
  
  let misurazioniData = {};
  if (Array.isArray(misurazioniPDF)) {
    const misurazioniPerSezione = misurazioniPDF.reduce((acc, misurazione) => {
      if (!acc[misurazione.sezione]) {
        acc[misurazione.sezione] = [];
      }
      acc[misurazione.sezione].push({
        name: misurazione.nome,
        value: misurazione.valore
      });
      return acc;
    }, {});
    misurazioniData = misurazioniPerSezione;
  } else {
    misurazioniData = misurazioniPDF || {};
  }
  
  const hasMisurazioni = Object.keys(misurazioniData).length > 0;
  
  if (!hasMisurazioni) return null;
  
  // Sezioni per la seconda pagina
  const sezioniSecondaPagina = ['M-Mode e PW', '2D'];
  const altreSezioni = Object.keys(misurazioniData).filter(sezione => 
    !['Mitrale', 'Tricuspide', 'Aorta', 'Polmonare', 'M-Mode e PW', '2D'].includes(sezione)
  );
  const sezioniSecondaPaginaPresenti = [...sezioniSecondaPagina.filter(sezione => (misurazioniData as any)[sezione]), ...altreSezioni];
  
  if (sezioniSecondaPaginaPresenti.length === 0) return null;
  
  return (
    <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '24px 24px 12px 24px', borderRadius: '8px' }}>
      <h2 className="mb-3" style={{ 
        fontSize: '18px', 
        fontWeight: 'bold', 
        color: '#1891a6' 
      }}>
        Misurazioni (continua)
      </h2>
      
      {sezioniSecondaPaginaPresenti.map((section) => {
        const measurements = (misurazioniData as any)[section];
        if (!Array.isArray(measurements) || measurements.length === 0) return null;
        
        return (
          <div key={section} className="mb-3">
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <h3 className="mb-2" style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: '#1891a6' 
              }}>
                {section}
              </h3>
              <div className="row">
                {measurements.map((measurement: any, idx: number) => (
                  <div key={idx} className="col-md-4 col-lg-3 mb-2">
                    <div style={{ padding: '8px' }}>
                      <span style={{ fontWeight: '500' }}>{measurement.name}:</span><br />
                      <span style={{ 
                        fontWeight: '700', 
                        color: '#106675' 
                      }}>
                        {measurement.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
})()}

          {/* Sezione Firma Medico */}
          <div className="mb-4" style={{ backgroundColor: '#e3f2fd', padding: '8px 24px 8px 24px', borderRadius: '8px' }}>
            <h2 className="mb-3" style={{ 
              fontSize: '18px', 
              fontWeight: 'bold', 
              color: '#1891a6' 
            }}>
              Firma del Medico
            </h2>
            
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '16px 20px 18px',
              borderRadius: '8px',
              border: '2px dashed #1891a6',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '8px'
              }}>
                Firma del Medico
              </div>
              <img
                src={firmaPng}
                alt="Firma Medico"
                style={{
                  display: 'block',
                  width: '260px',
                  height: '78px',
                  margin: '0 auto',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.warn('Errore nel caricamento della firma');
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div style={{
                width: '260px',
                margin: '4px auto 8px',
                borderBottom: '1px solid #1891a6'
              }} />
              <div style={{
                fontSize: '12px',
                color: '#1891a6',
                fontWeight: 'bold'
              }}>
                Dr. {getMedicoName()}
              </div>
            </div>
          </div>

          {/* PDF Allegato */}
          {(referto as any)?.file && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-primary">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0 text-uppercase"><strong>📎 PDF Allegato</strong></h6>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => window.open((referto as any).file, '_blank')}
                        className="me-2"
                      >
                        📎 Visualizza PDF Allegato
                      </Button>
                      <small className="text-muted">Clicca per aprire il PDF caricato</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Footer informativo migliorato */}
          <div className="row mt-5">
            <div className="col-12">
              <div className="card bg-light border-0">
                <div className="card-body">
                  <hr className="my-2" />
                  <div className="text-center">
                    <small className="text-muted">
                      <em>Referto medico veterinario generato elettronicamente - Documento valido ai sensi della normativa vigente</em>
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RefertoPDF;
